<template>
  <div>
    <div class="title-container">
      <h1 class="title">
        {{ title }}
      </h1>
      <div class="config-name-container">
        <PVInputText
          v-if="isEditingName"
          ref="nameInputRef"
          v-model="config.name"
          class="config-name"
          fluid
          :invalid="!nameInputState"
          :on-blur="() => toggleNameEditing(false)"
          @keydown.enter.prevent="() => toggleNameEditing(false)"
          @keydown.esc.prevent="() => toggleNameEditing(false)"
        />
        <h1 v-else class="config-name" @click="toggleNameEditing()">
          {{ config.name }}
        </h1>
        <font-awesome-icon
          v-if="!isEditingName"
          icon="pencil"
          @click="toggleNameEditing"
        />
      </div>
    </div>
    <div class="config-description-container">
      <PVTextarea
        v-if="isEditingDescription"
        ref="descriptionInputRef"
        v-model="config.description"
        class="config-description"
        fluid
        :on-blur="() => toggleDescriptionEditing(false)"
        @keydown.enter.prevent="() => toggleDescriptionEditing(false)"
        @keydown.esc.prevent="() => toggleDescriptionEditing(false)"
      />
      <div
        v-else
        class="config-description"
        @click="toggleDescriptionEditing()"
      >
        {{ config.description }}
      </div>
      <font-awesome-icon
        v-if="!isEditingDescription"
        icon="pencil"
        @click="toggleDescriptionEditing"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, watch, onMounted, nextTick, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import { SuppliersService } from '@/services';
  import { ICreateCheckBotConfigurationDto } from 'types';
  import PVInputText from 'primevue/inputtext';
  import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
  import PVTextarea from 'primevue/textarea';

  const { t } = useI18n();
  const { createErrorToast } = useToast();

  const props = defineProps<{
    isCreatingNewConfig: boolean;
    modelValue: ICreateCheckBotConfigurationDto;
    supplierId?: string;
  }>();

  const emit = defineEmits<{
    (e: 'update:modelValue', payload: ICreateCheckBotConfigurationDto): void;
  }>();

  const config = ref<ICreateCheckBotConfigurationDto>({
    name: '',
    description: '',
    checks: [],
  });
  const isEditingDescription = ref(false);
  const isEditingName = ref(false);
  const isFetchingSupplierName = ref(false);
  const supplierName = ref<string | null>(null);

  const title = computed(() => {
    const titleText = t(
      'checkBotConfiguratorHeader.checkBotConfiguration',
    ).toString();
    return supplierName.value
      ? `${titleText}  (${supplierName.value})`
      : titleText;
  });

  const nameInputState = computed(() => !!config.value.name);

  async function fetchSupplierName() {
    if (!props.supplierId) return;
    isFetchingSupplierName.value = true;
    try {
      supplierName.value = await SuppliersService.getSupplierName(
        props.supplierId,
      );
    } catch {
      createErrorToast(
        t('checkBotConfiguratorHeader.errors.errorGettingSupplierInfo'),
      );
    } finally {
      isFetchingSupplierName.value = false;
    }
  }

  const nameInputRef = ref<InstanceType<typeof PVInputText> | null>(null);
  function toggleNameEditing(toEditMode = true) {
    /** Cannot leave name empty */
    if (!nameInputState.value) return;
    isEditingName.value = toEditMode;
    if (isEditingName.value) {
      nextTick(() => {
        (nameInputRef.value as any).$el.focus();
      });
    }
  }

  const descriptionInputRef = ref('descriptionInput');
  function toggleDescriptionEditing(toEditMode = true) {
    isEditingDescription.value = toEditMode;
    if (isEditingDescription.value) {
      nextTick(() => {
        (descriptionInputRef.value as any).$el.focus();
      });
    }
  }

  watch(
    () => props.modelValue,
    (newValue) => {
      config.value = newValue;
    },
    { immediate: true },
  );

  watch(
    config,
    (newConfig) => {
      emit('update:modelValue', newConfig);
    },
    { deep: true },
  );

  onMounted(() => {
    fetchSupplierName();
    if (props.isCreatingNewConfig) toggleNameEditing();
  });
</script>

<style lang="scss" scoped>
  .fa-pencil {
    color: $electric-yellow;
    cursor: pointer;
  }
  .title-container {
    margin-bottom: 1.5rem;
    .title {
      margin-bottom: 0.5rem;
      font-weight: 500;
    }
    .config-name-container {
      display: flex;
      align-items: center;
      column-gap: 1rem;
      .config-name {
        font-size: 3.125rem;
        font-weight: 500;
        color: inherit;
        padding-left: 0;
        line-height: 1;
      }
    }
  }
  .config-description-container {
    display: flex;
    align-items: center;
    column-gap: 1rem;
    .config-description {
      font-size: 1.25rem;
      color: inherit;
      padding-left: 0;
    }
  }
</style>
